/* eslint-disable @typescript-eslint/no-unused-expressions */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from 'src/components/layouts/Modal/Modal';
import Input from 'src/components/ui/Input/Input';
import Textarea from 'src/components/ui/Textarea/Textarea';
import Button, { ButtonToneProps } from 'src/components/ui/Button/Button';
import Toggle from 'src/components/ui/Toggle/Toggle';

export type SaveWorkflowModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSave: (title: string, description: string, oneTime: boolean) => Promise<void> | void;
  initialTitle?: string;
  initialDescription?: string;
  initialOneTime?: boolean;
  setTitle?: React.Dispatch<React.SetStateAction<string>>;
  setDescription?: React.Dispatch<React.SetStateAction<string>>;
  setOneTime?: React.Dispatch<React.SetStateAction<boolean>>;
  isLoading?: boolean;
  isEditMode?: boolean;
};

const SaveWorkflowModal: React.FC<SaveWorkflowModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialTitle = '',
  initialDescription = '',
  initialOneTime = true,
  setTitle: setParentTitle,
  setDescription: setParentDescription,
  setOneTime: setParentOneTime,
  isLoading = false,
  isEditMode = false,
}) => {
  const { t } = useTranslation();
  const [localTitle, setLocalTitle] = useState(initialTitle);
  const [localDescription, setLocalDescription] = useState(initialDescription);
  const [localOneTime, setLocalOneTime] = useState(initialOneTime);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!setParentTitle) setLocalTitle(initialTitle);
    if (!setParentDescription) setLocalDescription(initialDescription);
    if (!setParentOneTime) setLocalOneTime(initialOneTime);
    setSubmitted(false);
  }, [initialTitle, initialDescription, initialOneTime, setParentTitle, setParentDescription, setParentOneTime]);

  const oneTime = setParentOneTime ? initialOneTime : localOneTime;
  const handleOneTimeToggle = (value: boolean) => {
    setParentOneTime ? setParentOneTime(value) : setLocalOneTime(value);
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setParentTitle ? setParentTitle(e.target.value) : setLocalTitle(e.target.value);
  };

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setParentDescription ? setParentDescription(e.target.value) : setLocalDescription(e.target.value);
  };

  const handleSave = async () => {
    setSubmitted(true);
    const currentTitle = setParentTitle ? initialTitle : localTitle;
    const currentDescription = setParentDescription ? initialDescription : localDescription;
    if (!currentTitle) {
      return;
    }
    await onSave(currentTitle, currentDescription, oneTime);
  };

  const handleContinueEditing = () => {
    onClose();
  };

  const handleClose = () => {
    if (setParentTitle) setParentTitle(initialTitle);
    if (setParentDescription) setParentDescription(initialDescription);
    if (setParentOneTime) setParentOneTime(initialOneTime);
    setSubmitted(false);
    onClose();
  };

  const modalTitle = isEditMode ? t('workflows.edit_workflow') : t('workflows.save_workflow');

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <div className="w-[460px]">
        <div className="border-subtle-bottom p-32">
          <h4 className="mb-24 typo-title-24">{modalTitle}</h4>

          <div className="mb-20">
            <Input
              label={t('workflows.title')}
              placeholder={t('workflows.enter_title')}
              value={setParentTitle ? initialTitle : localTitle}
              onChange={handleTitleChange}
              disabled={isLoading}
              error={submitted && !(setParentTitle ? initialTitle : localTitle)}
              errorMessage={t('workflows.title_required')}
            />
          </div>

          <div className="mb-8">
            <Textarea
              label={t('workflows.description')}
              placeholder={t('workflows.enter_description')}
              value={setParentDescription ? initialDescription : localDescription}
              onChange={handleDescriptionChange}
              rows={4}
              maxRows={6}
              disabled={isLoading}
            />
          </div>
          <div className="flex flex-col gap-y-8 mt-16">
            <Toggle
              checked={oneTime}
              label={t('workflows.one_time_label')}
              onToggle={handleOneTimeToggle}
              disabled={isLoading}
              horizontalLabel
            />
            <span className="typo-text-12 text-faded">{t('workflows.one_time_help')}</span>
          </div>
        </div>

        <div className="py-16 px-24 flex justify-end gap-12">
          <Button
            onClick={handleContinueEditing}
            tone={ButtonToneProps.SECONDARY}
            title={t('workflows.continue_editing')}
            disabled={isLoading}
          />
          <Button
            onClick={handleSave}
            tone={ButtonToneProps.PRIMARY_BLUE}
            title={t('general.save')}
            disabled={isLoading || !(setParentTitle ? initialTitle : localTitle).trim()}
          />
        </div>
      </div>
    </Modal>
  );
};

export default SaveWorkflowModal;
